import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Icon, Modal } from "semantic-ui-react";

import { AddPinModalForm } from "components/PinModal/components/AddPinModalForm";
import "components/PinModal/PinModal.scss";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { PIN_STATUSES, PIN_TYPES, WARRANTY_TYPES } from "models";

import type { PinModelWithHistory } from "components";
import type { AddPinModalFormValues } from "components/PinModal/components/AddPinModalForm";
import type { Dealer, Pin } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components";

type AddPinModalProps = {
  isOpen: boolean;
  pin: PinModelWithHistory;
  dealer?: Dealer | null;
  readOnly?: boolean;
  isSavingPin?: boolean;
  isDeleteButtonDisabled: boolean;
  onClose: () => void;
  openPrintOverviewModal: () => void;
  openDeleteConfirmationModal: () => void;
  onSavePin?: (payload: Pin) => Promise<void>;
};

const FormHelper = createFormHelper<AddPinModalFormValues>();

export const AddPinModal = ({
  pin,
  dealer,
  isOpen,
  onClose,
  openPrintOverviewModal,
  openDeleteConfirmationModal,
  onSavePin,
  isSavingPin = false,
  isDeleteButtonDisabled,
  readOnly = false
}: AddPinModalProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();

  const [hasErrors, setHasErrors] = useState(false);
  const [attachments, setAttachments] = useState<NoteAttachment[]>([]);

  const validateForm = (values: AddPinModalFormValues) => {
    const isInvalid = !pin.appointment_id || !values.pin_type_id || (values.visible_important_items && !values.note && !values.keep_parts);

    if (isInvalid) setHasErrors(true);
    return isInvalid;
  };

  const onSubmit = async (values: AddPinModalFormValues) => {
    if (validateForm(values)) return;

    const { note_suggestion: _noteSuggestion, pin_type_id } = values;
    // // TODO: Replace try/catch once pin mutations are handled via a shared hook (usePin).
    if (!onSavePin) return;
    try {
      await onSavePin({
        pin_type_id: pin_type_id ?? PIN_TYPES.Remarks, // cannot happen, but the compiler does not see that we validated it
        support_nr: values.support_nr,
        claim_nr: values.claim_nr,
        ref_nr: values.ref_nr,
        note: values.note,
        keep_parts: values.keep_parts,
        visible_important_items: values.visible_important_items,
        order_status: values.order_status,
        warranty_type_id: values.warranty_type_id || WARRANTY_TYPES.NotSet,
        pin_status_id: values.pin_status_id || PIN_STATUSES.NotSet,
        note_attachments: attachments.map(a => a.url),
        intervention_id: pin.intervention_id,
        appointment_id: pin.appointment_id,
        user_id: user?.id || 0,
        question_result_id: pin.question_result_id,
        created_on: pin.created_on,
        updated_on: pin.updated_on,
        id: pin.id,
        info: pin.info,
        mechanic_fixed: pin.mechanic_fixed,
        is_dms: pin.is_dms,
        dealer_location_id: pin.dealer_location_id,
        car_id: pin.car_id,
        manufacturer_id: pin.manufacturer_id,
        recalls_id: pin.recalls_id,
        pon_recall_id: pin.pon_recall_id,
        rdw_recall_id: pin.rdw_recall_id
      });
      onClose();
    } catch {
      toast.error(t("v8_failed_to_save_pin", "Failed to save pin"));
    }
  };

  if (pin.pin_status_id === PIN_STATUSES.Deleted) return null;

  return (
    <Modal open={isOpen} size="large" className="PinModal" closeOnDimmerClick onClose={onClose}>
      <FormHelper.Form
        onSubmit={onSubmit}
        defaultValues={{
          pin_type_id: pin.pin_type_id,
          support_nr: pin.support_nr || "",
          claim_nr: pin.claim_nr || "",
          ref_nr: pin.ref_nr || "",
          order_status: pin.order_status || null,
          warranty_type_id: pin.warranty_type_id || null,
          pin_status_id: pin.pin_status_id || null,
          note: pin.note,
          keep_parts: pin.keep_parts,
          visible_important_items: pin.visible_important_items,
          note_suggestion: undefined
        }}
        formProps={{ loading: isSavingPin }}
      >
        <div className="PinModal_layout">
          <div className="PinModal_header">
            <p className="PinModal_title">
              <Icon className="pin" />
              {t("v8_pin_item", "Pin Item")}
            </p>

            <Button color="grey" className="-appointment-status" onClick={openPrintOverviewModal} type="button">
              <Icon className="print black" />
              {t("v8_print_overview", "Print Overview")}
            </Button>
          </div>

          <AddPinModalForm
            pin={pin}
            dealer={dealer}
            readOnly={readOnly}
            hasErrors={hasErrors}
            attachments={attachments}
            isSavingPin={isSavingPin}
            isDeleteButtonDisabled={isDeleteButtonDisabled}
            onClose={onClose}
            setAttachments={setAttachments}
            openDeleteConfirmationModal={openDeleteConfirmationModal}
          />
        </div>
      </FormHelper.Form>
    </Modal>
  );
};
